/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  MouseEventHandler,
  useCallback,
  useEffect,
  useRef
} from 'react';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import {translate} from '@ion/core/src/locale/locale';
import {FloatingIconButtonGroup} from '@ion/dls/src/components/buttons/FloatingIconButton';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {useIsPhone, useMedia} from '@ion/dls/src/hooks/useMedia';
import {SavedMapViewsIcon} from '@ion/dls/src/icons/SavedMapViewsIcon';
import SavedMapViewsChooser from '@ion/dls/src/maps/components/views/SavedMapViewsChooser';
import IonPortal from '@ion/dls/src/render/IonPortal';
import {IconSize} from '@ion/dls/src/types/types';
import {addComponent, removeComponent} from '../helpers/simpleMapUtils';
import {useShowTool} from '../hooks/useShowTool';
import {MapToolPositions, MapTools} from '../types/types';

// defines the Saved MapTool prop definitions...
export interface SavedMapToolProps{
  isSelected?: boolean;
  onClick: MouseEventHandler;
  size: IconSize;
};

// defines the prop definition for SavedMapViewToolFactory component...
export interface SavedMapViewToolFactoryProps {
  view: __esri.MapView;
  position: MapToolPositions;
  onSaveMapView?: any;
  savedMapViews?: any;
  deleteSavedMapView?: any;
  editSavedMapView?: any;
  setDefaultMapView?: Function;
  setSavedMapView?: any;
  activeTool: MapTools;
  setActiveTool: Function;
  topOffset?: number;
  rightOffset?: number;
};

/**
 * The SavedMapViewTool provides the UI for save map view
 *
 * ```typescript
 *
 *   <SavedMapViewTool size={} onClick={function() {}} />
 *
 * ```
 */
export const SavedMapViewTool = ({size, onClick, isSelected = false}: SavedMapToolProps) => (
  <FloatingIconButtonGroup>
    <FloatingIconButtonGroup.FloatingIconButton onClick={onClick} isSelected={isSelected}>
      <SavedMapViewsIcon size={size} />
    </FloatingIconButtonGroup.FloatingIconButton>
  </FloatingIconButtonGroup>
);

/**
 * The SavedMapViewToolFactory component is responsible for creating a saved map view tool
 *
 * ```typescript
 *
 *  import SavedMapViewToolFactory from '../tools/SavedMapViewTool';
 *
 *  <SavedMapViewToolFactory view={view} />
 *
 * ```
 */
export const SavedMapViewToolFactory = ({
  view,
  topOffset,
  rightOffset,
  position,
  activeTool,
  setActiveTool,
  onSaveMapView,
  deleteSavedMapView,
  editSavedMapView,
  setDefaultMapView,
  setSavedMapView,
  savedMapViews
}: SavedMapViewToolFactoryProps) => {
  const childRef                               = useRef(null);
  const isPhone                                = useIsPhone();
  const [show, toggleShow, handleClickOutside] = useShowTool({
    ref    : childRef,
    toolId : MapTools.savedMap,
    activeTool,
    setActiveTool
  });
  const {isDesktop} = useMedia();

  const onCloseSavedMapView = useCallback(() => {
    toggleShow();
  }, [toggleShow]);

  useEffect(() => {
    if (show) {
      addComponent({
        id        : MapTools.savedMapView,
        component : (
          <ClickAwayListener onClickAway={handleClickOutside}>
            <div ref={childRef}>
              <IonPortal id='save-map-view-tool' top={topOffset} show={show}>
                <SavedMapViewsChooser view={view}
                  onSaveMapView={onSaveMapView}
                  toggleSavedMapViewTool={onCloseSavedMapView}
                  savedMapViews={savedMapViews}
                  isOpeningFromBottomSheet={!isDesktop}
                  overideStyle={{
                    top      : isDesktop ? null : 48,
                    position : isDesktop ? null : 'relative'
                  }}
                  deleteSavedMapView={deleteSavedMapView}
                  editSavedMapView={editSavedMapView}
                  setDefaultMapView={setDefaultMapView}
                  closeMapTools={onCloseSavedMapView}
                  setSavedMapView={setSavedMapView}
                  rightOffset={rightOffset}
                  isVisible
                  showHeaderAlways />
              </IonPortal>
            </div>
          </ClickAwayListener>
        ),
        position,
        view,
        index : 4
      });
    } else {
      removeComponent(MapTools.savedMapView, view);
    }
  }, [onCloseSavedMapView, show, topOffset, rightOffset, savedMapViews]);

  return (
    <Tooltip title={translate('maps:controls.savedMapViews.title')} renderOnlyChildren={isPhone}>
      <div>
        <SavedMapViewTool size={IconSize.medium}
          onClick={toggleShow}
          isSelected={show} />
      </div>
    </Tooltip>
  );
};

export default SavedMapViewToolFactory;
